import React, { forwardRef, useRef, useImperativeHandle, useState, useEffect } from "react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { FORM_ACTIONS } from "../FormActions";
import styles from "./TranslationTableForm.module.sass";
import { SovosBox, SovosTextField, SovosFilterTextEntry } from "mosaic-react";

const TranslationTableForm = (props, ref) => {
    const { data, organizations, status, onSave, onChange } = props;
    const {
        name,
        description,
        entity,
        organizationId,
        datasetLocale,
        id,
        isDuplicateMode,
        delimiter,
    } = data || {};
    const formRef = useRef();
    const { i18n, getTranslationTableDatasetLocaleAndEntities } = useGlobalValue();

    const [datasetLocales, setDatasetLocales] = useState([]);
    const [entities, setEntities] = useState([]);

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                        },
                    },
                },
            });
    };

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));

    function statusCheck(modalStatus) {
        return modalStatus !== "loading";
    }

    const getCountries = async (organizationID) => {
        const data = await getTranslationTableDatasetLocaleAndEntities(organizationID);
        const datasetLocales = data ? data["datasetLocales"] : [];
        setDatasetLocales(datasetLocales);
    };

    const getEntities = async (datasetLocale) => {
        const data = await getTranslationTableDatasetLocaleAndEntities("", datasetLocale);
        const entities = data ? data["entities"] : [];
        setEntities(entities);
    };

    const isToDisableDatasetLocaleInput = () => {
        return (
            (Array.isArray(datasetLocales) && datasetLocales.length === 0) ||
            (id && !isDuplicateMode)
        );
    };
    const isToDisableEntityInput = () => {
        return (Array.isArray(entities) && entities.length === 0) || (id && !isDuplicateMode);
    };

    useEffect(() => {
        setEntities([]);
        if (organizationId) {
            getCountries(organizationId);
        } else {
            setDatasetLocales([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    useEffect(() => {
        setEntities([]);

        if (datasetLocale) {
            getEntities(datasetLocale);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasetLocale]);

    return statusCheck(status) ? (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (formRef.current.checkValidity()) {
                    if (isDuplicateMode) {
                        handleChange(e, "isDuplicateMode", false);
                    }
                    onSave && onSave();
                } else {
                    formRef.current.reportValidity();
                }
            }}
        >
            <SovosBox className={styles["group-content"]}>
                <SovosTextField
                    label={i18n`Name`}
                    placeholder={i18n`Name`}
                    fullWidth={true}
                    value={name || ""}
                    required
                    onChange={(e) => {
                        handleChange(e, "name", e.target.value);
                    }}
                />

                <SovosTextField
                    label={i18n`Description`}
                    onChange={(e) => {
                        handleChange(e, "description", e.target.value);
                    }}
                    placeholder={i18n`Description`}
                    value={description || ""}
                    fullWidth={true}
                    multiline={true}
                    rows={3}
                />

                <SovosTextField
                    sx={{ marginTop: "1rem" }}
                    label={i18n`Delimiter`}
                    placeholder={i18n`Delimiter`}
                    fullWidth={true}
                    value={delimiter || ""}
                    onChange={(e) => {
                        handleChange(e, "delimiter", e.target.value);
                    }}
                />

                <SovosFilterTextEntry
                    sx={{ '& .MuiTextField-root': { minHeight: '56px !important' } }}
                    label={i18n`Organization`}
                    data={[
                        organizationId && organizations?.length
                            ? organizations.find(({ id }) => id === organizationId)
                            : { id: "", name: "" },
                    ]}
                    AutocompleteProps={{
                        loading: organizations?.length === 0,
                        options: organizations || [],
                        getOptionLabel: (option) => option.name,
                        isOptionEqualToValue: (option, value) => option.value === value.id,
                        disabled: id && !isDuplicateMode ? true : false,
                    }}
                    onChange={(e) => {
                        const [eventValue] = e;
                        handleChange(e, "organizationId", eventValue.id);
                    }}
                    required
                />
            </SovosBox>

            <SovosBox className={styles["group-content"]}>
                <SovosFilterTextEntry
                    sx={{ '& .MuiTextField-root': { minHeight: '56px !important' } }}
                    label={i18n`Dataset Locale`}
                    data={[
                        datasetLocale && datasetLocales
                            ? datasetLocales.find(({ value }) => value === datasetLocale)
                            : { value: "", name: "" },
                    ]}
                    AutocompleteProps={{
                        loading: datasetLocales?.length === 0,
                        options: datasetLocales || [],
                        getOptionLabel: (option) => option.name,
                        isOptionEqualToValue: (option, value) => option.value === value.value,
                        disabled: isToDisableDatasetLocaleInput() || false,
                    }}
                    onChange={(e) => {
                        const [eventValue] = e;
                        handleChange(e, "datasetLocale", eventValue.value);
                    }}
                    required
                />
            </SovosBox>

            <SovosBox className={styles["group-content"]}>
                <SovosFilterTextEntry
                    sx={{ '& .MuiTextField-root': { minHeight: '56px !important' } }}
                    label={i18n`Entity`}
                    data={[
                        entity && entities
                            ? entities.find(({ value }) => value === entity)
                            : { value: "", name: "" },
                    ]}
                    AutocompleteProps={{
                        loading: entities?.length === 0,
                        options: entities || [],
                        getOptionLabel: (option) => option.name,
                        isOptionEqualToValue: (option, value) => option.value === value.value,
                        disabled: isToDisableEntityInput() || false,
                    }}
                    onChange={(e) => {
                        const [eventValue] = e;
                        handleChange(e, "entity", eventValue.value);
                    }}
                    required
                />
            </SovosBox>
        </form>
    ) : null;
};

const TranslationTableFormRef = forwardRef(TranslationTableForm);

TranslationTableFormRef.queries = (args) => {
    return [
        {
            resource: "translation_table_user_organization",
            body: "__typename,id,name",
        },
        args && args.id
            ? {
                resource: "translation_table",
                args: {
                    translationTableId: +args.id || 0,
                    isDuplicateMode: `${args.isDuplicateMode}`,
                },
                body: "__typename,id,name,description,entity,organizationId,datasetLocale,isDuplicateMode,execution,delimiter",
            }
            : undefined,
    ].filter((q) => q);
};

TranslationTableFormRef.dataMapper = ["organizations", "data"];

TranslationTableFormRef.actions = [FORM_ACTIONS.SAVE];

export default TranslationTableFormRef;
